import { useEffect, useMemo, useState, useCallback } from "react";
import styles from "./OurEventPage.module.less";

import akkBackground from "app/assets/akkBackground.webp";
import { useParams } from "react-router";
import Loader from "shared/components/Loader";
import { ArchiveType, useGetArchivesQuery } from "api/ticketsApi";
import Image from "shared/components/Image";
import useEmblaCarousel from "embla-carousel-react";

interface Props {
  setBackgroundImage: (src: string) => void;
}

const OurEventPage = ({ setBackgroundImage }: Props) => {
  const { eventId } = useParams();
  const { data: items, isLoading } = useGetArchivesQuery(undefined);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [currentYear, setCurrentYear] = useState<string>("");
  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false);
  const [loadedImages, setLoadedImages] = useState<Record<string, boolean>>({});

  // Initialize main carousel
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: "start"
  });

  // Initialize thumbnail carousel
  const [thumbsRef, thumbsApi] = useEmblaCarousel({
    loop: false,
    align: "start",
    containScroll: "keepSnaps"
  });

  useEffect(() => {
    setBackgroundImage(akkBackground);
  }, [setBackgroundImage]);

  // Group archives by year with proper typing and reverse years
  const groupedArchives = useMemo(() => {
    if (!items) return {} as Record<string, ArchiveType[]>;

    const grouped = items.reduce((acc, archive) => {
      const year = archive.years;
      if (!acc[year]) {
        acc[year] = [];
      }
      acc[year].push(archive);
      return acc;
    }, {} as Record<string, ArchiveType[]>);

    // Sort years in descending order (newest first)
    const sortedYears = Object.keys(grouped).sort((a, b) => parseInt(b) - parseInt(a));

    // Rebuild object with sorted years
    const reversedGrouped: Record<string, ArchiveType[]> = {};
    sortedYears.forEach(year => {
      reversedGrouped[year] = grouped[year];
    });

    return reversedGrouped;
  }, [items]);

  // Set initial year when data loads (now will be the newest year)
  useEffect(() => {
    if (items && items.length > 0 && !currentYear) {
      const years = Object.keys(groupedArchives);
      if (years.length > 0) {
        setCurrentYear(years[0]); // First year is now the newest
      }
    }
  }, [items, groupedArchives, currentYear]);

  // Get current year's archives
  const currentArchives = currentYear ? groupedArchives[currentYear] || [] : [];

  // Handle thumbnail click
  const onThumbClick = useCallback((index: number) => {
    setSelectedIndex(index);
    if (emblaApi) {
      emblaApi.scrollTo(index);
    }
  }, [emblaApi]);

  // Sync selected index with main carousel and update button states
  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setPrevBtnEnabled(emblaApi.canScrollPrev());
    setNextBtnEnabled(emblaApi.canScrollNext());
  }, [emblaApi, setSelectedIndex]);

  // Set up event listeners for carousel
  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
  }, [emblaApi, onSelect]);

  // Navigation handlers
  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  // Handle year change
  const handleYearChange = (year: string) => {
    setCurrentYear(year);
    setSelectedIndex(0);
    setLoadedImages({}); // Reset loaded images when year changes
    if (emblaApi) {
      emblaApi.scrollTo(0);
    }
  };

  // Handle image load
  const handleImageLoad = useCallback((imageId: string) => {
    setLoadedImages(prev => ({
      ...prev,
      [imageId]: true
    }));
  }, []);

  // Preload adjacent images
  useEffect(() => {
    if (!currentArchives.length || !emblaApi) return;

    const currentIndex = selectedIndex;
    const preloadIndices = [
      currentIndex,
      currentIndex + 1,
      currentIndex - 1
    ].filter(index => index >= 0 && index < currentArchives.length);

    preloadIndices.forEach(index => {
      const archive = currentArchives[index];
      if (archive && !loadedImages[`${currentYear}-${archive.id}`]) {
        const img = document.createElement('img');
        img.src = archive.imgCover || archive.img;
      }
    });
  }, [selectedIndex, currentArchives, currentYear, loadedImages, emblaApi]);

  if (!items || isLoading) {
    return <Loader />;
  }

  return (
      <div className={styles.container}>
        <h1>Архив событий</h1>

        {/* Year navigation - now showing in reverse order */}
        <div className={styles.yearNavigation}>
          {Object.keys(groupedArchives).map((year) => (
              <button
                  key={year}
                  className={`${styles.yearButton} ${currentYear === year ? styles.yearButtonActive : ''}`}
                  onClick={() => handleYearChange(year)}
              >
                {year} год
              </button>
          ))}
        </div>

        {/* Current year section */}
        {currentYear && currentArchives.length > 0 && (
            <div className={styles.yearSection}>
              <h2 className={styles.yearTitle}>{currentYear} год</h2>

              {/* Main Carousel with Navigation */}
              <div className={styles.embla}>
                <div className={styles.emblaViewport} ref={emblaRef}>
                  <div className={styles.emblaContainer}>
                    {currentArchives.map((archive) => {
                      const imageId = `${currentYear}-${archive.id}`;
                      const isLoaded = loadedImages[imageId];
                      const imageSrc = archive.imgCover || archive.img;

                      return (
                          <div className={styles.emblaSlide} key={imageId}>
                            {!isLoaded && (
                                <div className={styles.emblaSlideLoader}>
                                  <div className={styles.spinner} />
                                </div>
                            )}
                            {/* Use native img element for onLoad support */}
                            <img
                                src={imageSrc}
                                alt={archive.name}
                                className={`${styles.emblaSlideImage} ${isLoaded ? styles.emblaSlideImageLoaded : ''}`}
                                onLoad={() => handleImageLoad(imageId)}
                                style={{ objectFit: 'contain' }}
                            />
                          </div>
                      );
                    })}
                  </div>
                </div>

                {/* Navigation Buttons */}
                <button
                    className={`${styles.emblaButton} ${styles.emblaButtonPrev}`}
                    onClick={scrollPrev}
                    disabled={!prevBtnEnabled}
                    type="button"
                >
                  <svg viewBox="0 0 24 24" width="24" height="24">
                    <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" fill="currentColor"/>
                  </svg>
                </button>
                <button
                    className={`${styles.emblaButton} ${styles.emblaButtonNext}`}
                    onClick={scrollNext}
                    disabled={!nextBtnEnabled}
                    type="button"
                >
                  <svg viewBox="0 0 24 24" width="24" height="24">
                    <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" fill="currentColor"/>
                  </svg>
                </button>
              </div>

              {/* Thumbnail Carousel */}
              <div className={styles.emblaThumbs}>
                <div className={styles.emblaThumbsViewport} ref={thumbsRef}>
                  <div className={styles.emblaThumbsContainer}>
                    {currentArchives.map((archive, index) => (
                        <div
                            className={`${styles.emblaThumbsSlide} ${index === selectedIndex ? styles.emblaThumbsSlideSelected : ''}`}
                            key={`${currentYear}-thumb-${archive.id}`}
                        >
                          <button
                              onClick={() => onThumbClick(index)}
                              className={styles.emblaThumbsSlideButton}
                              type="button"
                          >
                            <Image
                                src={archive.imgThumb || archive.img}
                                alt={`${archive.name} thumbnail`}
                                className={styles.emblaThumbsSlideImage}
                            />
                          </button>
                        </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
        )}
      </div>
  );
};

export default OurEventPage;